import { CommonConfigContext } from '@/contexts/ConfigContext';
import { GlobalUserDataContext } from '@/contexts/GlobalUserDataContext';
import { WebSocketContext } from '@/contexts/WebSocketContext';
import { CMDPsTokenUpdate } from '@/models/cmd/live';
import { getLiveDataState } from '@/modules/main/slice/liveData';
import { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getMainPlayerState, getMainUserState } from '../modules/main/selector';
import {
    calcLowHigh,
    mergeCurrencyName,
    numberFormat,
} from '../utils/commonFunc';
import { BalanceSummary } from './useTableBet';

export type useBalanceState = ReturnType<typeof useBalance>;
export const useBalance = () => {
    const { Balance, HoldingMoney, BalanceValid } =
        useSelector(getMainPlayerState);
    const { showCurrencyName: currencyConfig } =
        useContext(CommonConfigContext);
    const { CurrencyName: playerCurrencyName, LobbyCode: parentLobbyCode } =
        useSelector(getMainUserState);
    const [displayBalance, setDisplayBalance] = useState('-');
    const [balance, setBalance] = useState<number>();
    const [currency, setCurrency] = useState('');
    const { sendCommand } = useContext(WebSocketContext);
    const [balanceSummary, setBalanceSummary] = useState<BalanceSummary>();

    //for ABC pending amount
    const { isAutoConfirmEnabled } = useContext(GlobalUserDataContext);
    const { totalHostsBetInfo } = useSelector(getLiveDataState);
    useEffect(() => {
        const currentBalance = calcLowHigh(Balance);
        const currentHoldingMoney = calcLowHigh(HoldingMoney);

        // init the display balance before reading common config
        const b = BalanceValid
            ? (currentBalance - currentHoldingMoney) / 100
            : 0;
        // fix negative balance issue
        if (b < 0) {
            const tokenUpdate = new CMDPsTokenUpdate();
            sendCommand(tokenUpdate);
            setDisplayBalance('-');
        }
        let bal = 0;
        if (isAutoConfirmEnabled && totalHostsBetInfo) {
            const pending =
                (totalHostsBetInfo.pendingBetAmount +
                    totalHostsBetInfo.pendingWithHoldAmount) /
                100;
            bal = b - pending;
        } else {
            bal = b;
        }
        setBalance(bal);
        setBalanceSummary({
            serverBalance: currentBalance,
            serverHoldBalance: currentHoldingMoney,
            vaildBalance: bal * 100,
        });
    }, [
        Balance,
        HoldingMoney,
        isAutoConfirmEnabled,
        totalHostsBetInfo,
        BalanceValid,
    ]);
    useEffect(() => {
        // remove '2' in the currency name
        const currencyName = mergeCurrencyName(playerCurrencyName);
        setCurrency(currencyName);
        // show currency name logic
        try {
            if (currencyConfig) {
                const lobbyCode = parentLobbyCode.toLocaleLowerCase();
                for (const cc of currencyConfig) {
                    const excludeCurrency = cc['exCurrency'] ?? [];
                    const includedLobby = cc['include'] ?? [];
                    const excludeLobby = cc['exclude'] ?? [];

                    const isMatched =
                        excludeCurrency.includes(currencyName) ||
                        excludeCurrency[0] === 'all';

                    if (isMatched) {
                        const isMatchedAll = excludeLobby[0] === 'all';
                        const isMatchedInclude =
                            includedLobby.length > 0 &&
                            !includedLobby
                                .map(l => l.toLocaleLowerCase())
                                .includes(lobbyCode);
                        const isMatchedExclude =
                            excludeLobby.length > 0 &&
                            excludeLobby
                                .map(l => l.toLocaleLowerCase())
                                .includes(lobbyCode);
                        if (
                            isMatchedAll ||
                            isMatchedInclude ||
                            isMatchedExclude
                        ) {
                            // remove the currency name
                            setCurrency('');
                        }
                    }
                }
            }
        } catch (err) {
            console.error(
                'hook::use::balance',
                'cannot parse common config',
                err
            );
        }
    }, [playerCurrencyName, parentLobbyCode, currencyConfig]);
    useEffect(() => {
        if (balance !== undefined) {
            let db = numberFormat(Math.max(balance, 0));
            if (db.length > 30) db = db.replace(' ', '\n');
            setDisplayBalance(BalanceValid ? db : '-');
        }
    }, [balance, BalanceValid]);
    return { displayBalance, currency, balance, balanceSummary };
};
